"use client";
import { useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import { ArrowUp, ArrowDown, ChevronDown, ChevronUp } from "lucide-react";
import { formatDate } from "date-fns";
import {
   Table,
   Button,
   TableRow,
   TableBody,
   TableCell,
   TableHead,
   TableHeader
} from "@/app/components/ui";
import type { Top30CoinsTaiFuIndexByIndexResponse } from "@/types";
import { cn } from "@/app/lib/utils";

type SortField =
   | "rank"
   | "quarterStartMarketCapPercent"
   | "todayMarketCapPercent"
   | "positionValue"
   | "last"
   | "_24hChangePercent"
   | "_1MChangePercent"
   | "_3MChangePercent"
   | "_1YChangePercent";

type SortDirection = "asc" | "desc" | null;

interface TaiFuTopCoinTableProps {
   data: Top30CoinsTaiFuIndexByIndexResponse;
   highlightFirstRow?: boolean;
   defaultVisibleRows?: number;
}

const formatPercent = (value: number | null | undefined): string => {
   if (value === null || value === undefined) return "N/A";
   return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
};

const formatPercentThreeDecimals = (value: number | null | undefined): string => {
   if (value === null || value === undefined) return "N/A";
   return `${value.toFixed(3)}%`;
};

const formatUsd = (value: number | null | undefined): string => {
   if (value === null || value === undefined) return "N/A";
   return `$${value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
   })}`;
};

const formatPriceChange24h = (
   priceChange24h: number,
   priceChangePercent24h: number
): string => {
   const sign = priceChange24h >= 0 ? "+" : "";
   return `${sign}${formatUsd(priceChange24h)} (${formatPercent(priceChangePercent24h)})`;
};

export const TaiFuTopCoinTable: React.FC<TaiFuTopCoinTableProps> = ({
   data,
   highlightFirstRow = true
}) => {
   const translate = useTranslations("Chat.ChatMessageMetadata");
   const [sortField, setSortField] = useState<SortField>("rank");
   const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
   const [isExpanded, setIsExpanded] = useState(true);

   const handleSort = (field: SortField) => {
      if (sortField === field) {
         if (sortDirection === "asc") {
            setSortDirection("desc");
         } else if (sortDirection === "desc") {
            setSortDirection(null);
            setSortField("rank");
         }
      } else {
         setSortField(field);
         setSortDirection("asc");
      }
   };

   const sortedData = useMemo(() => {
      if (!sortDirection) {
         return [...(data?.coins ?? [])].sort((a, b) => a.rank - b.rank);
      }

      const sorted = [...(data?.coins ?? [])].sort((a, b) => {
         let aValue: number | null;
         let bValue: number | null;

         switch (sortField) {
            case "rank":
               aValue = a.rank;
               bValue = b.rank;
               break;
            case "quarterStartMarketCapPercent":
               aValue = a.quarterStartMarketCapPercent;
               bValue = b.quarterStartMarketCapPercent;
               break;
            case "todayMarketCapPercent":
               aValue = a.todayMarketCapPercent;
               bValue = b.todayMarketCapPercent;
               break;
            case "positionValue":
               aValue = a.positionValue;
               bValue = b.positionValue;
               break;
            case "last":
               aValue = a.last;
               bValue = b.last;
               break;
            case "_24hChangePercent":
               aValue = a._24hChangePercent;
               bValue = b._24hChangePercent;
               break;
            case "_1MChangePercent":
               aValue = a._1MChangePercent;
               bValue = b._1MChangePercent;
               break;
            case "_3MChangePercent":
               aValue = a._3MChangePercent;
               bValue = b._3MChangePercent;
               break;
            case "_1YChangePercent":
               aValue = a._1YChangePercent;
               bValue = b._1YChangePercent;
               break;
            default:
               return 0;
         }

         if (aValue === null || aValue === undefined) return 1;
         if (bValue === null || bValue === undefined) return -1;

         if (sortDirection === "asc") {
            return aValue - bValue;
         } else {
            return bValue - aValue;
         }
      });

      return sorted;
   }, [data, sortField, sortDirection]);

   if (sortedData.length === 0) {
      return null;
   }

   const SortIcon = ({ field }: { field: SortField }) => {
      if (sortField !== field) return null;
      if (sortDirection === "asc") {
         return <ArrowUp className="h-3 w-3 inline-block ml-1" />;
      } else if (sortDirection === "desc") {
         return <ArrowDown className="h-3 w-3 inline-block ml-1" />;
      }
      return null;
   };

   return (
      <div className="my-4 w-full">
         <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => setIsExpanded(!isExpanded)}>
               {isExpanded ? (
                  <>
                     <ChevronUp className="h-4 w-4" />
                     {translate("collapse")}
                  </>
               ) : (
                  <>
                     <ChevronDown className="h-4 w-4" />
                     {translate("expand")}
                  </>
               )}
            </Button>
         </div>
         {isExpanded && (
            <div className="overflow-x-auto">
               <Table>
                  <TableHeader>
                     <TableRow>
                        <TableHead
                           className="text-left cursor-pointer hover:bg-muted/50 transition-colors select-none"
                           onClick={() => handleSort("rank")}
                        >
                           <div className="flex items-center">
                              #
                              <SortIcon field="rank" />
                           </div>
                        </TableHead>
                        <TableHead className="text-left">Symbol</TableHead>
                        <TableHead
                           className="text-right cursor-pointer hover:bg-muted/50 transition-colors select-none"
                           onClick={() => handleSort("quarterStartMarketCapPercent")}
                        >
                           <div className="flex items-center justify-end gap-1">
                              <div>
                                 % Of Total
                                 <br />(
                                 {formatDate(
                                    new Date(data.firstDayOfQuater),
                                    "yyyy-MM-dd"
                                 )}
                                 )
                              </div>
                              <SortIcon field="quarterStartMarketCapPercent" />
                           </div>
                        </TableHead>
                        <TableHead
                           className="text-right cursor-pointer hover:bg-muted/50 transition-colors select-none"
                           onClick={() => handleSort("todayMarketCapPercent")}
                        >
                           <div className="flex items-center justify-end gap-1">
                              <div>
                                 % Of Total
                                 <br />
                                 (Today)
                              </div>
                              <SortIcon field="todayMarketCapPercent" />
                           </div>
                        </TableHead>
                        <TableHead
                           className="text-right cursor-pointer hover:bg-muted/50 transition-colors select-none"
                           onClick={() => handleSort("positionValue")}
                        >
                           <div className="flex items-center justify-end gap-1">
                              <div>
                                 Pos Value
                                 <br />
                                 (Points)
                              </div>
                              <SortIcon field="positionValue" />
                           </div>
                        </TableHead>
                        <TableHead
                           className="text-right cursor-pointer hover:bg-muted/50 transition-colors select-none"
                           onClick={() => handleSort("last")}
                        >
                           <div className="flex items-center justify-end">
                              Last
                              <SortIcon field="last" />
                           </div>
                        </TableHead>
                        <TableHead
                           className="text-right cursor-pointer hover:bg-muted/50 transition-colors select-none"
                           onClick={() => handleSort("_24hChangePercent")}
                        >
                           <div className="flex items-center justify-end">
                              24h Chg
                              <SortIcon field="_24hChangePercent" />
                           </div>
                        </TableHead>
                        <TableHead
                           className="text-right cursor-pointer hover:bg-muted/50 transition-colors select-none"
                           onClick={() => handleSort("_1MChangePercent")}
                        >
                           <div className="flex items-center justify-end">
                              1Mo Chg
                              <SortIcon field="_1MChangePercent" />
                           </div>
                        </TableHead>
                        <TableHead
                           className="text-right cursor-pointer hover:bg-muted/50 transition-colors select-none"
                           onClick={() => handleSort("_3MChangePercent")}
                        >
                           <div className="flex items-center justify-end">
                              3Mo Chg
                              <SortIcon field="_3MChangePercent" />
                           </div>
                        </TableHead>
                        <TableHead
                           className="text-right cursor-pointer hover:bg-muted/50 transition-colors select-none"
                           onClick={() => handleSort("_1YChangePercent")}
                        >
                           <div className="flex items-center justify-end">
                              1Y Chg
                              <SortIcon field="_1YChangePercent" />
                           </div>
                        </TableHead>
                     </TableRow>
                  </TableHeader>
                  <TableBody>
                     {sortedData.map((item, index) => {
                        const isNegative24h = item._24hChangePercent < 0;
                        const isNegative1M =
                           item._1MChangePercent !== null && item._1MChangePercent < 0;
                        const isNegative3M =
                           item._3MChangePercent !== null && item._3MChangePercent < 0;
                        const isNegative1Y =
                           item._1YChangePercent !== null && item._1YChangePercent < 0;

                        const isHighlighted = highlightFirstRow && index === 0;

                        return (
                           <TableRow
                              key={`${item.coinId}-${item.rank}`}
                              className={cn({
                                 "bg-yellow-100 dark:bg-yellow-900/20": isHighlighted
                              })}
                           >
                              <TableCell className="font-medium">{item.rank}</TableCell>
                              <TableCell>
                                 {item.name || "N/A"} ({item.symbol || "N/A"})
                              </TableCell>
                              <TableCell className="text-right">
                                 {formatPercentThreeDecimals(
                                    item.quarterStartMarketCapPercent
                                 )}
                              </TableCell>
                              <TableCell className="text-right">
                                 {formatPercentThreeDecimals(item.todayMarketCapPercent)}
                              </TableCell>
                              <TableCell className="text-right">
                                 {item.positionValue.toFixed(2)}
                              </TableCell>
                              <TableCell className="text-right">
                                 {formatUsd(item.last)}
                              </TableCell>
                              <TableCell
                                 className={cn("text-right", {
                                    "text-red-500 dark:text-red-400": isNegative24h,
                                    "text-green-600 dark:text-green-400": !isNegative24h
                                 })}
                              >
                                 {formatPriceChange24h(
                                    item._24hChange,
                                    item._24hChangePercent
                                 )}
                              </TableCell>
                              <TableCell
                                 className={cn("text-right", {
                                    "text-red-500 dark:text-red-400": isNegative1M,
                                    "text-green-600 dark:text-green-400": !isNegative1M
                                 })}
                              >
                                 {item._1MChangePercent !== null
                                    ? formatPercent(item._1MChangePercent)
                                    : "N/A"}
                              </TableCell>
                              <TableCell
                                 className={cn("text-right", {
                                    "text-red-500 dark:text-red-400": isNegative3M,
                                    "text-green-600 dark:text-green-400": !isNegative3M
                                 })}
                              >
                                 {item._3MChangePercent !== null
                                    ? formatPercent(item._3MChangePercent)
                                    : "N/A"}
                              </TableCell>
                              <TableCell
                                 className={cn("text-right", {
                                    "text-red-500 dark:text-red-400": isNegative1Y,
                                    "text-green-600 dark:text-green-400": !isNegative1Y
                                 })}
                              >
                                 {item._1YChangePercent !== null
                                    ? formatPercent(item._1YChangePercent)
                                    : "N/A"}
                              </TableCell>
                           </TableRow>
                        );
                     })}
                  </TableBody>
               </Table>
            </div>
         )}
      </div>
   );
};
